import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getRecipe,
  deleteRecipe,
  aiSubstitute,
  parseWeight,
  calculateDailyValues,
  aggregateIngredients,
  scaleNutritionFacts,
  EMPTY_NUTRITION,
} from '@recipe/shared';
import { ArrowLeft, Users, BookOpen, Edit, Trash2, Flame, Candy, Loader2, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import AIChatModal from '../components/AIChatModal';
import NutritionFacts from '../components/NutritionFacts';
import './RecipeDetail.css';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desiredServings, setDesiredServings] = useState(4);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState(new Set());
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [substituteTarget, setSubstituteTarget] = useState(null);
  const [substitutes, setSubstitutes] = useState([]);
  const [isSubstituting, setIsSubstituting] = useState(false);
  const deletingRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const fetchRecipe = async () => {
    try {
      const data = await getRecipe(id);
      if (data) {
        setRecipe(data);
      } else {
        if (!deletingRef.current) {
          toast.error('食谱未找到');
        }
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      if (!deletingRef.current) {
        toast.error('加载食谱失败');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  useEffect(() => {
    // Set desired servings to original recipe servings when recipe is loaded
    if (recipe) {
      setDesiredServings(recipe.servings || 4);
    }
  }, [recipe]);

  const handleSuggestSubstitute = async (ingredientName) => {
    setSubstituteTarget(ingredientName);
    setIsSubstituting(true);
    setSubstitutes([]);
    try {
      const results = await aiSubstitute(ingredientName);
      setSubstitutes(results || []);
    } catch (e) {
      console.error('Error suggesting substitute:', e);
      toast.error('获取替换建议失败');
    } finally {
      setIsSubstituting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>正在加载食谱...</p>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="container">
        <div className="error-page">
          <h2>食谱未找到</h2>
          <p>您查找的食谱不存在。</p>
          <Link to="/" className="btn btn-primary">返回首页</Link>
        </div>
      </div>
    );
  }

  // Calculate adjusted weights based on desired servings
  const calculateAdjustedWeightForDisplay = (originalWeight, originalServings, desiredServings) => {
    const ratio = desiredServings / originalServings;
    return Math.round(originalWeight * ratio * 10) / 10; // Round to 1 decimal place
  };

  // Handle edit recipe
  const handleEdit = () => {
    navigate(`/edit-recipe/${id}`, { state: { recipe } });
  };

  // Handle delete recipe
  const handleDelete = async () => {
    try {
      deletingRef.current = true;
      await deleteRecipe(id);
      toast.success('食谱删除成功！');
      navigate('/');
    } catch (error) {
      toast.error('删除食谱失败');
    }
  };

  // Handle checkbox toggle
  const handleCheckboxToggle = (ingredientKey) => {
    setCheckedIngredients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ingredientKey)) {
        newSet.delete(ingredientKey);
      } else {
        newSet.add(ingredientKey);
      }
      return newSet;
    });
  };

  return (
    <div className="container">
      <div className="recipe-detail">
        <div className="recipe-header">
          <div className="header-top">
            <Link to="/" className="back-btn">
              <ArrowLeft size={20} />
              返回食谱列表
            </Link>
          </div>

          <div className="recipe-hero">
            <div className="recipe-info">
              <h1>{recipe.title}</h1>
              <p className="recipe-description">{recipe.description}</p>

              <div className="servings-calculator">
                <div className="servings-input-group">
                  <label htmlFor="desired-servings">调整份量：</label>
                  <input
                    type="number"
                    id="desired-servings"
                    value={desiredServings}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setDesiredServings('');
                      } else {
                        const numValue = parseInt(value);
                        if (numValue > 0) {
                          setDesiredServings(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 1) {
                        setDesiredServings(1);
                      }
                    }}
                    min="1"
                    className="servings-input"
                  />
                  <span className="servings-label">{recipe.servingUnit || '份'}</span>
                </div>

              </div>

              <div className="recipe-meta-grid">
                <div className="meta-item">
                  <Users size={20} />
                  <div>
                    <span className="meta-label">原始食谱份量</span>
                    <span className="meta-value">{recipe.servings || '4'} {recipe.servingUnit || '人份'}</span>
                  </div>
                </div>

                {recipe.category && (
                  <div className="meta-item">
                    <BookOpen size={20} />
                    <div>
                      <span className="meta-label">分类</span>
                      <span className="meta-value">{recipe.category}</span>
                    </div>
                  </div>
                )}

                {recipe.nutritionFacts && (
                  <>
                    <div className="meta-item">
                      <Flame size={20} />
                      <div>
                        <span className="meta-label">卡路里</span>
                        <span className="meta-value">{Math.round((recipe.nutritionFacts.calories || 0) * (desiredServings / (recipe.servings || 1)))} kcal</span>
                      </div>
                    </div>

                    <div className="meta-item">
                      <Candy size={20} />
                      <div>
                        <span className="meta-label">糖分</span>
                        <span className="meta-value">{Math.round((recipe.nutritionFacts.sugar || 0) * (desiredServings / (recipe.servings || 1)) * 10) / 10}g</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {recipe.parts ? (
          // New structured format
          <div className="recipe-parts">
            {/* Table of Contents - Now at the top */}
            <div className="table-of-contents">
              <h2>目录</h2>
              <ul className="toc-list">
                {recipe.parts.map((part, partIndex) => (
                  <li key={partIndex} className="toc-item">
                    <a
                      href={`#part-${partIndex}`}
                      className="toc-link"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(`part-${partIndex}`).scrollIntoView({
                          behavior: 'smooth'
                        });
                      }}
                    >
                      {part.name || `Part ${partIndex + 1}`}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Total Ingredients Section - Now below table of contents */}
            <div className="total-ingredients-section">
              <h2>所需总配料</h2>
              {(() => {
                const sortedIngredients = aggregateIngredients(recipe.parts);

                const nutrition = recipe.nutritionFacts
                  ? scaleNutritionFacts(recipe.nutritionFacts, recipe.servings || 1, desiredServings)
                  : EMPTY_NUTRITION;

                const dailyValues = calculateDailyValues(nutrition);

                return (
                  <>
                    <ul className="total-ingredients-list">
                      {sortedIngredients.map(([name, weightData]) => {
                        const ingredientKey = `total-${name}`;
                        const isChecked = checkedIngredients.has(ingredientKey);
                        const adjustedWeight = calculateAdjustedWeightForDisplay(
                          weightData.number,
                          recipe.servings || 4,
                          desiredServings
                        );
                        let displayText;
                        if (weightData.number === 0 && weightData.raw) {
                          displayText = weightData.raw;
                        } else if (weightData.mixedUnits) {
                          displayText = `${adjustedWeight}${weightData.unit} (mixed units)`;
                        } else {
                          displayText = `${adjustedWeight}${weightData.unit}`;
                        }

                        return (
                          <li key={name} className="total-ingredient-item">
                            <span
                              className={`ingredient-checkbox ${isChecked ? 'checked' : ''}`}
                              onClick={() => handleCheckboxToggle(ingredientKey)}
                            ></span>
                            <span className="ingredient-text">
                              {name} - {displayText}
                            </span>
                            <button 
                              className="substitute-btn"
                              onClick={() => handleSuggestSubstitute(name)}
                              title="寻找替换食材"
                            >
                              <RefreshCw size={14} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Nutrition Facts Table */}
                    <div className="nutrition-section">
                      <h2>营养成分表</h2>
                      <NutritionFacts
                        nutrition={nutrition}
                        dailyValues={dailyValues}
                        servings={desiredServings}
                        servingUnit={recipe.servingUnit || '份'}
                      />
                      <div className="nutrition-disclaimer">
                        <p>数据来源：Gemini 3 AI 计算，仅供参考，不保证准确性</p>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Recipe Parts and Steps */}
            {recipe.parts.map((part, partIndex) => (
              <div key={partIndex} id={`part-${partIndex}`} className="recipe-part">
                <h2 className="part-title">{part.name || `Part ${partIndex + 1}`}</h2>

                <div className="part-content">
                  {part.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="recipe-step">
                      <div className="step-header">
                        <h3>步骤 {stepIndex + 1}</h3>
                      </div>

                      <div className={`step-content ${(!step.ingredients || step.ingredients.filter(ing => ing.name && ing.name.trim() !== '').length === 0) ? 'no-ingredients' : ''}`}>
                        <div className="step-ingredients">

                          {step.ingredients && step.ingredients.length > 0 && (
                            <ul className="ingredients-list">
                              {step.ingredients
                                .filter(ingredient => ingredient.name && ingredient.name.trim() !== '')
                                .map((ingredient, ingredientIndex) => {
                                  const { number, unit, raw } = parseWeight(ingredient.weight);
                                  const adjustedWeight = calculateAdjustedWeightForDisplay(
                                    number,
                                    recipe.servings || 4,
                                    desiredServings
                                  );

                                  // Show raw text for unparseable weights, otherwise adjusted value
                                  const weightDisplay = (number === 0 && raw) ? raw : `${adjustedWeight}${unit}`;

                                  const ingredientKey = `step-${partIndex}-${stepIndex}-${ingredientIndex}`;
                                  const isChecked = checkedIngredients.has(ingredientKey);
                                  return (
                                    <li key={ingredientIndex} className="ingredient-item">
                                      <span
                                        className={`ingredient-checkbox ${isChecked ? 'checked' : ''}`}
                                        onClick={() => handleCheckboxToggle(ingredientKey)}
                                      ></span>
                                      <span className="ingredient-text">
                                        {ingredient.name} - {weightDisplay}
                                      </span>
                                      <button 
                                        className="substitute-btn"
                                        onClick={() => handleSuggestSubstitute(ingredient.name)}
                                        title="寻找替换食材"
                                      >
                                        <RefreshCw size={14} />
                                      </button>
                                    </li>
                                  );
                                })}
                            </ul>
                          )}
                        </div>

                        <div className="step-instructions">

                          <p className="instruction-text">{step.details}</p>
                          {step.imageUrl && (
                            <div className="step-image-display" onClick={() => setFullscreenImage(step.imageUrl)}>
                              <img src={step.imageUrl} alt={`步骤 ${stepIndex + 1}`} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Legacy format for old recipes
          <div className="recipe-content-grid">
            <div className="ingredients-section">
              <h2>配料</h2>
              <ul className="ingredients-list">
                {recipe.ingredients?.map((ingredient, index) => {
                  const ingredientKey = `legacy-${index}`;
                  const isChecked = checkedIngredients.has(ingredientKey);
                  return (
                    <li key={index} className="ingredient-item">
                      <span
                        className={`ingredient-checkbox ${isChecked ? 'checked' : ''}`}
                        onClick={() => handleCheckboxToggle(ingredientKey)}
                      ></span>
                      <span className="ingredient-text">{ingredient}</span>
                      <button 
                        className="substitute-btn"
                        onClick={() => handleSuggestSubstitute(ingredient)}
                        title="寻找替换食材"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="instructions-section">
              <h2>制作步骤</h2>
              <ol className="instructions-list">
                {recipe.instructions?.map((instruction, index) => (
                  <li key={index} className="instruction-item">
                    <span className="step-number">{index + 1}</span>
                    <div className="instruction-text">{instruction}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {recipe.notes && (
          <div className="notes-section">
            <h2>Notes</h2>
            <p>{recipe.notes}</p>
          </div>
        )}

        {/* Recipe Actions - Moved to bottom */}
        <div className="recipe-actions-bottom">
          <button 
            onClick={() => navigate(`/cooking/${id}?servings=${desiredServings}`, { state: { desiredServings } })} 
            className="action-btn cooking-mode-btn"
          >
            <BookOpen size={16} />
            烹饪模式
          </button>
          <button onClick={handleEdit} className="action-btn edit-btn">
            <Edit size={16} />
            编辑食谱
          </button>
          <button onClick={() => setShowDeleteConfirm(true)} className="action-btn delete-btn">
            <Trash2 size={16} />
            删除食谱
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <h3>删除食谱</h3>
            <p>您确定要删除 "{recipe.title}" 吗？此操作无法撤销。</p>
            <div className="modal-actions">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
              >
                删除食谱
              </button>
            </div>
          </div>
        </div>
      )}
      {fullscreenImage && (
        <div className="image-fullscreen-overlay" onClick={() => setFullscreenImage(null)}>
          <button className="image-fullscreen-close" onClick={() => setFullscreenImage(null)}>✕</button>
          <img
            src={fullscreenImage}
            alt="全屏图片"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Substitute Modal */}
      {substituteTarget && (
        <div className="modal-overlay substitute-modal-overlay" onClick={() => setSubstituteTarget(null)}>
          <div className="substitute-modal" onClick={(e) => e.stopPropagation()}>
            <div className="substitute-header">
              <h3>"{substituteTarget}" 的替换建议</h3>
              <button 
                type="button" 
                className="close-btn"
                onClick={() => setSubstituteTarget(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="substitute-content">
              {isSubstituting ? (
                <div className="substitute-loading">
                  <Loader2 size={24} className="spin" />
                  <p>AI 正在分析并寻找合适的替代食材...</p>
                </div>
              ) : substitutes.length > 0 ? (
                <div className="substitutes-list">
                  {substitutes.map((item, idx) => (
                    <div key={idx} className="substitute-item">
                      <h4>{item.name}</h4>
                      <p>{item.reason}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="substitutes-empty">
                  <p>暂无建议的替代食材</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeDetail;
